import { useFlags as useLDFlags, type LDFlagSet } from "launchdarkly-react-client-sdk"

import { useOptionalUser } from "~/providers/userProvider"
import { useFeatureFlagOverridesStore } from "~/features/feature-flags/client/featureFlagOverrides"
import { HAKIO_FLAGS_SWITCH, type FeatureFlagKey } from "~/features/feature-flags/shared/registry"
import { useHakioFlagsStore } from "~/features/feature-flags/client/hakioFlagsStore"

/** Always read from LaunchDarkly: it has to work while our own app or database is down. */
const LAUNCHDARKLY_ONLY_FLAGS = ["maintenanceMode", HAKIO_FLAGS_SWITCH] as const
type LaunchDarklyOnlyKey = (typeof LAUNCHDARKLY_ONLY_FLAGS)[number]

/** What `useFlags` returns. Typed so the compiler rejects reads of keys that aren't flags. */
export type FlagValues = Readonly<Partial<Record<FeatureFlagKey | LaunchDarklyOnlyKey, boolean>>>

/**
 * Drop-in replacement for `useFlags` from `launchdarkly-react-client-sdk` that applies
 * session-scoped local overrides on top of the flag values.
 *
 * The in-house flags always load; the app holds its first render until they arrive
 * (`useHakioFlagsPending` in `_auth.tsx`), and if loading fails the last ones loaded for this
 * company and user apply, else the registry defaults. While the `hakioFeatureFlags` LaunchDarkly
 * switch is off, LaunchDarkly's value wins for every flag LaunchDarkly has; once it is on, only the
 * in-house values apply. `maintenanceMode` always comes from LaunchDarkly.
 *
 * Always import `useFlags` from `~/features/feature-flags` (this file) — not directly from
 * `launchdarkly-react-client-sdk`. Importing the raw hook bypasses both the in-house flags and
 * the devtools override panel.
 *
 * Overrides are set via the "Feature Flags" panel in the TanStack devtools (Hakio
 * users only, Cmd/Ctrl+Shift+A). They persist in sessionStorage for the current
 * browser tab only and never touch LaunchDarkly or affect other users. They apply to Hakio staff
 * only, so a customer can't switch flags on by writing that sessionStorage key.
 */
export function useFlags(): FlagValues {
  const flags = useFlagsWithoutOverrides()
  const overrides = useFeatureFlagOverridesStore((s) => s.overrides)
  const isStaff = useOptionalUser()?.permissions.isStaff ?? false
  // The one untyped boundary: LaunchDarkly and the override store hold arbitrary string keys.
  return (isStaff ? { ...flags, ...overrides } : flags) as FlagValues
}

/** Flag values before devtools overrides. Only the devtools panel needs this. */
export function useFlagsWithoutOverrides(): LDFlagSet {
  return mergeFlagSources(
    useLDFlags(),
    useHakioFlagsStore((state) => state.flags),
  )
}

/** Switch off: LaunchDarkly decides the flags it has, and flags made since come from the in-house
 * system. Switch on: the in-house system decides all but the LaunchDarkly-only flags. */
export function mergeFlagSources(
  ldFlags: LDFlagSet,
  hakioFlags: Readonly<Record<string, boolean>> | undefined,
): LDFlagSet {
  if (!hakioFlags) return ldFlags
  if (ldFlags[HAKIO_FLAGS_SWITCH] !== true) return { ...hakioFlags, ...ldFlags }
  const launchDarklyOnly = Object.fromEntries(
    LAUNCHDARKLY_ONLY_FLAGS.map((key) => [key, ldFlags[key]]),
  )
  return { ...hakioFlags, ...launchDarklyOnly }
}
